import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, ReactNode, useState } from 'react';
import styled from 'styled-components';

import {
  base64DecodeCustomCode,
  base64EncodeCustomCode,
} from 'dashboard/components/GridLayoutRender/computed-properties/helpers';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import { generateJsonSchemaCompletionSource } from 'dashboard/components-lib/CodeEditor/completionSources/javascript/jsonSchemaCompletionSource';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { Insight, ModuleInsightsSummary } from 'dashboard/modules/InsightsSummary/model';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import { JsonSchemaObject } from 'lib/json-schema';

const Container = styled('div')`
  padding: ${Spacing[5]};
`;

export interface InsightsSummaryPanelProps {
  module: ModuleInsightsSummary;
  dataSchema: JsonSchemaObject;
  updateModule: (module: ModuleInsightsSummary) => void;
}

const InsightsSummaryPanel = ({ module, dataSchema, updateModule }: InsightsSummaryPanelProps) => {
  const { insights } = module;

  const handleUpdateInsights = (insights: Insight[]) => {
    updateModule({
      ...module,
      insights,
    });
  };

  const handleUpdateLabel = (e: ChangeEvent<HTMLInputElement>) => {
    updateModule({
      ...module,
      label: e.target.value,
    });
  };

  return (
    <Container>
      <Label size='md' weight='bold'>
        Card name
      </Label>
      <Input type='text' value={module.label} onChange={handleUpdateLabel} />
      <Spacer height={2} />
      <Label size='md' weight='bold'>
        Insights
      </Label>
      <ListBox
        items={insights}
        onChange={handleUpdateInsights}
        renderRow={(insight) => <span>{insight.label}</span>}
        configPanelTitle='Edit insight'
        renderConfigPanel={(insight, handleItemChange) => (
          <CodeEditor
            code={JSON.stringify(base64DecodeCustomCode(insight), null, 2)}
            onChange={(insightJSON) => {
              handleItemChange(base64EncodeCustomCode(JSON.parse(insightJSON)));
            }}
            language='jsonjs'
            completionSources={{
              javascript: [generateJsonSchemaCompletionSource(dataSchema)],
            }}
            editable
          />
        )}
        defaultItem={() =>
          ({
            label: `New insight ${insights.length + 1}`,
            states: [
              {
                icon: 'check',
                color: '#35A260',
                // TODO(brandon): make creation of computed properties easier
                condition: {
                  'resolution-mechanism': {
                    'resolution-type': 'template-literal',
                    'serialized-code': btoa('{{ true }}'),
                  },
                },
                'display-value': {
                  'resolution-mechanism': {
                    'resolution-type': 'template-literal',
                    'serialized-code': btoa(
                      `State ${insights.flatMap((insight) => insight.states).length + 1}`,
                    ),
                  },
                },
              },
            ],
          }) as Insight
        }
      />
    </Container>
  );
};

const ListBoxContainer = styled.div`
  border-radius: ${({ theme }) => theme.element.borderRadius};
  border: 1px solid ${({ theme }) => theme.color.border.input};
  padding: ${Spacing[1]};
`;

const ListBoxItemWrapper = styled.div<{ selected: boolean }>`
  cursor: pointer;
  padding: ${Spacing[1]} ${Spacing[2]};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  color: ${({ theme, selected }) => (selected ? theme.color.variants.primary.default : 'inherit')};
  background-color: ${({ theme, selected }) =>
    selected ? theme.color.variants.primary.highlightLight : 'inherit'};
  transition-duration: ${TransitionDuration.Fast};
  user-select: none;
`;

const ListBoxButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: ${Spacing[1]};
`;

const ConfigPanelContainer = styled.div`
  width: 900px;
`;

interface ListBoxProps<T> {
  items: T[];
  onChange: (items: T[]) => void;
  renderRow: (item: T) => ReactNode;
  configPanelTitle: string;
  renderConfigPanel: (item: T, handleItemChange: (item: T) => void) => ReactNode;
  defaultItem: () => T;
}

function ListBox<T>({
  items,
  onChange,
  renderRow,
  configPanelTitle,
  renderConfigPanel,
  defaultItem,
}: ListBoxProps<T>) {
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  const handleItemChange = (item: T) => {
    const updatedItems = [...items];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updatedItems[selectedItemIndex] = item;
    onChange(updatedItems);
  };

  return (
    <div>
      <ListBoxContainer>
        {items.map((item, i) => {
          return (
            <ListBoxItemWrapper
              selected={i === selectedItemIndex}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              onClick={() => setSelectedItemIndex(i)}
              key={JSON.stringify(item)}
            >
              {renderRow(item)}
            </ListBoxItemWrapper>
          );
        })}
      </ListBoxContainer>
      <Spacer height={1} />
      <ListBoxButtonContainer>
        <Button
          type='button'
          width='auto'
          variant='basic'
          id='list_box__add-item'
          onClick={() => onChange([...items, defaultItem()])}
        >
          Add
        </Button>
        <Button
          type='button'
          width='auto'
          variant='basic'
          id='list_box__delete-item'
          disabled={selectedItemIndex == null}
          onClick={() => {
            onChange(items.filter((_, i) => i !== selectedItemIndex));
            setSelectedItemIndex(null);
          }}
        >
          Delete
        </Button>
      </ListBoxButtonContainer>
      {selectedItemIndex == null ? null : (
        <MaximizablePanel
          title={configPanelTitle}
          disableMaximize
          offsetElementId='control-panel'
          outsideExceptionalIdPrefixes={['list_box__delete-item']}
          onOutsideClickAndEscPress={() => setSelectedItemIndex(null)}
        >
          <ConfigPanelContainer>
            {renderConfigPanel(items[selectedItemIndex], handleItemChange)}
          </ConfigPanelContainer>
        </MaximizablePanel>
      )}
    </div>
  );
}

export default InsightsSummaryPanel;
